import { useEffect, useState } from 'react';
import { Address } from 'shared/types/account';
import ValidateAddress from '@/components/customercenter-ui/common/validateAddress/validateAddress';
import ToolTips from '@/components/customercenter-ui/tooltip';
import AddressToolTip from '@/components/customercenter-ui/tooltip/address';
import PhoneNumberTooltip from '@/components/customercenter-ui/tooltip/phoneNumber';
import { Checkbox } from '@/components/customercenter-ui/ui/checkbox';
import { getCookieZipCode } from '@/components/customercenter-ui/utils/cookieUtils';
import { useCheckout } from '@/context/checkoutProvider';
import InputField from '@/customercenter-ui/common/validateForm/inputField';
import { StateDropDown } from '@/customercenter-ui/common/validateForm/stateDropDown';
import AddCircle from '@/customercenter-ui/icons/addCircle';
import { maskPhoneNumber, maskPostalCode } from '@/customercenter-ui/utils/formatUtils';
import useIntuitiveSearch from '@/frontastic/hooks/useIntuitiveSearch';
import { mapLocaleToMeaningfulFormat } from '@/helpers/utils/i18n';

const ShippingForm = ({
  errors,
  register,
  setValue,
  getValues,
  clearErrors,
  isShippingForm,
  email,
  onSubmit,
  verifyAddressRef,
  pageName,
  setIsLoading = () => {},
  onPaymentPage,
  trigger,
}: any) => {
  const {
    saveAddress,
    setSaveAddressFlag,
    saveBillingAddress,
    setSaveBillingAddressFlag,
    setShippingZipCode,
    shippingZipCode,
    setCurrentAddress,
  } = useCheckout();

  const loggedIn = false;
  const [pickListSelectedAddress, setPickListSelectedAddress] = useState();
  const [initialStateValue, setInitialStateValue] = useState('');
  const [showadditionalAddressInfo, setShowadditionalAddressInfo] = useState(false);
  const [postalCode, setPostalCode] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [streetName, setStreetName] = useState('');
  const [state, setState] = useState('');
  const [additionalAddressInfo, setAdditionalAddressInfo] = useState('');

  const handleShowAdditionalAddressInfo = () => {
    setShowadditionalAddressInfo(true);
  };

  const handlePostalCodeInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPostalCode(maskPostalCode(event.target.value));
  };

  const handleStreetNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setStreetName(event.target.value);
  };

  const handleAdditionalAddressInfoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAdditionalAddressInfo(event.target.value);
  };

  const handleStateChange = (value: string) => {
    setState(value);
  };

  const handlePhoneNumberInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPhoneNumber(maskPhoneNumber(event.target.value));
  };

  useEffect(() => {
    if (postalCode?.length >= 5 && !onPaymentPage) {
      const cookieZipCode = getCookieZipCode()?.slice(0, 5);
      const inputPostalCode = postalCode?.slice(0, 5);
      if (shippingZipCode && inputPostalCode !== shippingZipCode) {
        setShippingZipCode(inputPostalCode);
      } else if (!shippingZipCode && cookieZipCode && inputPostalCode !== cookieZipCode) {
        setShippingZipCode(inputPostalCode);
      }
    }
  }, [postalCode]);

  useEffect(() => {
    if (!onPaymentPage) {
      const currentAddress: Address = {
        streetName,
        additionalAddressInfo,
        state,
      };
      setCurrentAddress(currentAddress);
    }
  }, [streetName, state, additionalAddressInfo]);

  useIntuitiveSearch({
    setShowadditionalAddressInfo,
    setValue,
    setPostalCode,
    setInitialStateValue,
    clearErrors,
    setPickListSelectedAddress,
    setStreetName,
    setAdditionalAddressInfo,
    setState,
  });

  return (
    <>
      <ValidateAddress
        formValues={getValues}
        pickListSelectedAddress={pickListSelectedAddress}
        onSubmit={onSubmit}
        getValues={getValues}
        verifyAddressRef={verifyAddressRef}
        pageName={pageName}
        setIsLoading={setIsLoading}
      />
      <section className="flex w-full items-center pt-6">
        <section className="w-full lg:max-w-[400px]">
          <InputField
            items={mapLocaleToMeaningfulFormat().firstName}
            id={mapLocaleToMeaningfulFormat().firstNameId}
            htmlFor={mapLocaleToMeaningfulFormat().firstNameId}
            register={register}
            errors={errors}
            label="firstName"
            focusFirstName={true}
            classname="w-full"
            maxlength={28}
            onChange={() => {
              if (getValues('lastName')) {
                trigger(['firstName', 'lastName']);
              }
            }}
          />
        </section>
      </section>
      <section className="flex w-full items-center pt-2">
        <section className="w-full lg:max-w-[400px]">
          <InputField
            items={mapLocaleToMeaningfulFormat().lastName}
            id={mapLocaleToMeaningfulFormat().lastNameId}
            htmlFor={mapLocaleToMeaningfulFormat().lastNameId}
            register={register}
            errors={errors}
            label="lastName"
            classname="w-full"
            maxlength={28}
            onChange={() => {
              if (getValues('firstName')) {
                trigger(['firstName', 'lastName']);
              }
            }}
          />
        </section>
      </section>
      <section className="flex w-full gap-x-2 pt-2">
        <section className="w-full lg:max-w-[400px]">
          <InputField
            items={mapLocaleToMeaningfulFormat().addressLine1}
            id={mapLocaleToMeaningfulFormat().addressId}
            htmlFor={mapLocaleToMeaningfulFormat().addressId}
            register={register}
            errors={errors}
            label="streetName"
            classname="w-full address-input placeholder:text-white focus:placeholder:text-black"
            maxlength={30}
            onChange={handleStreetNameChange}
          />
        </section>
        <ToolTips className="mt-5" Content={{ ContentToolTip: <AddressToolTip /> }} />
      </section>
      {!showadditionalAddressInfo && (
        <button type="button" className="flex items-center gap-x-2 pb-5" onClick={handleShowAdditionalAddressInfo}>
          <AddCircle className="h-5 w-5" />
          <p className="text-base font-normal leading-5">{mapLocaleToMeaningfulFormat().addressLine2}</p>
          <p className="text-sm font-normal leading-5">({mapLocaleToMeaningfulFormat().optionalText})</p>
        </button>
      )}
      <section className={`${!showadditionalAddressInfo ? 'hidden ' : 'block'} flex w-full items-center pt-2`}>
        <section className="w-full lg:max-w-[400px]">
          <InputField
            items={mapLocaleToMeaningfulFormat().addressLine2}
            id={mapLocaleToMeaningfulFormat().additionalAddressInfo}
            htmlFor={mapLocaleToMeaningfulFormat().additionalAddressInfo}
            register={register}
            errors={errors}
            label="additionalAddressInfo"
            classname="w-full addressLine2"
            maxlength={30}
            onChange={handleAdditionalAddressInfoChange}
          />
        </section>
      </section>
      <section className="flex w-full items-center">
        <section className="w-full lg:max-w-[400px]">
          <InputField
            items={mapLocaleToMeaningfulFormat().city}
            id={mapLocaleToMeaningfulFormat().cityId}
            htmlFor={mapLocaleToMeaningfulFormat().cityId}
            register={register}
            errors={errors}
            label="city"
            classname="w-full city"
            maxlength={30}
          />
        </section>
      </section>
      <section className="flex gap-x-5 pt-2 lg:w-full">
        <section className="w-full lg:max-w-[217px]">
          <StateDropDown
            setValue={setValue}
            errors={errors}
            label="state"
            classname="w-full state"
            initialState={initialStateValue}
            register={register}
            id="state"
            onChange={handleStateChange}
          />
        </section>
        <section className="w-full max-w-[136px] lg:max-w-[161px]">
          <InputField
            items={mapLocaleToMeaningfulFormat().zipCode}
            id={mapLocaleToMeaningfulFormat().zipCodeId}
            htmlFor={mapLocaleToMeaningfulFormat().zipCodeId}
            register={register}
            errors={errors}
            label="postalCode"
            classname="w-full zip"
            value={postalCode}
            maxlength={10}
            onChange={handlePostalCodeInputChange}
          />
        </section>
      </section>
      <section className="flex w-full gap-x-2 pt-2">
        <section className="w-full lg:max-w-[400px]">
          <InputField
            items={mapLocaleToMeaningfulFormat().phoneNumberItemOptional}
            id={mapLocaleToMeaningfulFormat().phoneNumberId}
            htmlFor={mapLocaleToMeaningfulFormat().phoneNumberId}
            register={register}
            errors={errors}
            label="phone"
            classname="w-full"
            value={phoneNumber}
            onChange={handlePhoneNumberInputChange}
            maxlength={14}
          />
        </section>
        <ToolTips className="mt-5" Content={{ ContentToolTip: <PhoneNumberTooltip /> }} />
      </section>
      <section className="hidden">
        <InputField
          id="country"
          htmlFor="country"
          register={register}
          errors={errors}
          label="country"
          classname="w-full country"
          value="US"
          type="hidden"
        />
      </section>
      {loggedIn && isShippingForm && (
        <section className="flex items-start space-x-2 pb-5">
          <Checkbox
            id="save_this_address"
            onCheckedChange={() => {
              isShippingForm ? setSaveAddressFlag(!saveAddress) : setSaveBillingAddressFlag(!saveBillingAddress);
            }}
            checked={isShippingForm ? saveAddress : saveBillingAddress}
          />
          <label htmlFor="save_this_address" className="-mt-1 px-[3px] text-base font-normal leading-6">
            Save this Address
          </label>
        </section>
      )}
      {loggedIn && !isShippingForm && (
        <section>
          <p className="mb-2 pt-8 text-base font-bold">{mapLocaleToMeaningfulFormat().contactinfo}</p>
          <p>{email}</p>
        </section>
      )}
    </>
  );
};

export default ShippingForm;
